import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { Layout, Menu, Avatar, Typography, Button, Popconfirm } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { LimsNavIcon } from '../components/LimsNavIcon';
import MerchantTopNavbar from './MerchantTopNavbar';
import '../components/Sidebar.css';
import '../App.css';

const { Sider } = Layout;
const { Text } = Typography;

/**
 * 商家端壳层：对齐实验室登录后布局（LabMind 侧栏 + 顶栏 + 内容区），仅暴露订单管理。
 */
const MerchantLayout: React.FC = () => {
  const { token, role, realName, username, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth <= 480;
      setIsMobile(mobile);
      if (mobile) setCollapsed(true);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!token) {
    return <Navigate to="/merchant/login" replace state={{ from: location }} />;
  }
  if (role !== '商家') {
    return <Navigate to="/merchant/login" replace />;
  }

  const toggleSidebar = () => setCollapsed((v) => !v);

  const handleLogout = () => {
    logout();
    localStorage.removeItem('merchantId');
    localStorage.removeItem('merchantName');
    localStorage.removeItem('merchantCode');
    navigate('/merchant/login');
  };

  const selectedKey = location.pathname.startsWith('/merchant/shop')
    ? '/merchant/shop'
    : location.pathname.startsWith('/merchant/billing')
      ? '/merchant/billing'
      : location.pathname.startsWith('/merchant/brand-discounts')
        ? '/merchant/brand-discounts'
      : location.pathname.startsWith('/merchant/after-sales')
        ? '/merchant/after-sales'
      : location.pathname.startsWith('/merchant/promo')
        ? '/merchant/promo'
        : '/merchant/orders';

  return (
    <div className="app-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="antd-sidebar lims-sidebar"
        width={256}
        collapsedWidth={64}
      >
        {!collapsed && (
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <div className="lims-logo-mark lims-logo-mark--img" aria-hidden>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/brand/labmind-logo.png`}
                  alt=""
                  className="lims-sidebar-logo-img"
                />
              </div>
              <div className="sidebar-brand-titles">
                <Text className="logo-text" strong>
                  LabMind
                </Text>
                <Text className="logo-tagline">Merchant</Text>
              </div>
            </div>
            <Button
              type="text"
              icon={<LimsNavIcon name="left_panel_close" sizePx={20} />}
              onClick={toggleSidebar}
              className="sidebar-toggle"
            />
          </div>
        )}

        {collapsed && (
          <div className="collapsed-expand-button">
            <Button
              type="text"
              icon={<LimsNavIcon name="left_panel_open" sizePx={20} />}
              onClick={toggleSidebar}
              className="expand-toggle"
              size="large"
            />
          </div>
        )}

        <Menu
          theme="light"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          className="sidebar-menu"
          items={[
            {
              key: '/merchant/orders',
              icon: <LimsNavIcon name="local_shipping" sizePx={20} />,
              label: (
                <Link
                  to="/merchant/orders"
                  className="menu-link"
                  onClick={() => {
                    if (isMobile && !collapsed) toggleSidebar();
                  }}
                >
                  订单管理
                </Link>
              )
            },
            {
              key: '/merchant/billing',
              icon: <LimsNavIcon name="receipt_long" sizePx={20} />,
              label: (
                <Link
                  to="/merchant/billing"
                  className="menu-link"
                  onClick={() => {
                    if (isMobile && !collapsed) toggleSidebar();
                  }}
                >
                  账单管理
                </Link>
              )
            },
            {
              key: '/merchant/after-sales',
              icon: <LimsNavIcon name="assignment_return" sizePx={20} />,
              label: (
                <Link
                  to="/merchant/after-sales"
                  className="menu-link"
                  onClick={() => {
                    if (isMobile && !collapsed) toggleSidebar();
                  }}
                >
                  售后管理
                </Link>
              )
            },
            {
              key: '/merchant/brand-discounts',
              icon: <LimsNavIcon name="percent" sizePx={20} />,
              label: (
                <Link
                  to="/merchant/brand-discounts"
                  className="menu-link"
                  onClick={() => {
                    if (isMobile && !collapsed) toggleSidebar();
                  }}
                >
                  品牌折扣
                </Link>
              )
            },
            {
              key: '/merchant/promo',
              icon: <LimsNavIcon name="campaign" sizePx={20} />,
              label: (
                <Link
                  to="/merchant/promo"
                  className="menu-link"
                  onClick={() => {
                    if (isMobile && !collapsed) toggleSidebar();
                  }}
                >
                  推广激励
                </Link>
              )
            },
            {
              key: '/merchant/shop',
              icon: <LimsNavIcon name="storefront" sizePx={20} />,
              label: (
                <Link
                  to="/merchant/shop"
                  className="menu-link"
                  onClick={() => {
                    if (isMobile && !collapsed) toggleSidebar();
                  }}
                >
                  商铺中心
                </Link>
              )
            }
          ]}
        />

        {!collapsed && (
          <div className="sidebar-footer">
            <div className="user-info">
              <Avatar
                size={40}
                icon={<LimsNavIcon name="storefront" sizePx={22} />}
                style={{
                  backgroundColor: 'rgba(0, 53, 95, 0.12)',
                  color: 'var(--lims-primary)'
                }}
              />
              <div className="user-details">
                <Text className="user-name" strong>
                  {realName || username || '商家'}
                </Text>
                <Text className="user-role" type="secondary">
                  商家
                </Text>
              </div>
              <Popconfirm
                title="确定要退出登录吗？"
                description="退出后将返回商家登录页"
                onConfirm={handleLogout}
                okText="确定"
                cancelText="取消"
                placement="topRight"
              >
                <Button
                  type="text"
                  icon={<LimsNavIcon name="logout" sizePx={18} />}
                  className="logout-button"
                  size="small"
                />
              </Popconfirm>
            </div>
          </div>
        )}
      </Sider>

      {isMobile && !collapsed && (
        <div className="sidebar-overlay" onClick={toggleSidebar} />
      )}

      <div className={`main-content ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <MerchantTopNavbar onToggleSidebar={toggleSidebar} />
        <div className="page-container">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default MerchantLayout;
